/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2023
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import {
  DataTable,
  DataTableSkeleton,
  Pagination,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableSelectRow,
  TableCell,
  TableRow,
  TableHeader
} from 'carbon-components-react';
import cn from 'classnames';
import _ from 'lodash';
import React, { useState, useEffect, useCallback } from 'react';
import { useIntl } from 'react-intl';

import { getSpeechPlanNameById } from '../../../helpers/speech-plan-helpers';
import { TABLE_PAGE_SIZES } from '../constants';
import { Speech as messages } from '../messages';

/**
 * Property definition of a modified speech instance inside the
 * instance-selection DataTable.
 */
interface SpeechRowInstance {
  /**
   * The instance's CRN ID
   */
  id: string;
  /**
   * The instance's CRN name
   */
  name: string;
  /**
   * The service plan associated to the speech instance
   */
  plan: string;
  /**
   * The resource group name associated to the speech instance
   */
  resource_group: string;
  /**
   * The account name or region associated to the speech instance
   */
  account: string;
  /**
   * The region associated to the speech instance
   */
  region: string;
}

interface SpeechInstanceTableProps {
  instances: any[];
  defaultSelection: string;
  handleSelect: (id: string) => void;
  loading: boolean;
  size?: string;
  readOnly: boolean;
}
/**
 * This is good to use for speech instances.
 * @param {object} props
 * @returns
 */
export const SpeechInstanceTable = ({
  instances,
  defaultSelection,
  handleSelect,
  loading,
  size = 'lg',
  readOnly
}: SpeechInstanceTableProps) => {
  // The parsed STT/TTS instances to be shown in the instance table.
  const [parsedInstances, setParsedInstances] = useState<SpeechRowInstance[]>(
    []
  );
  // The selected row or STT/TTS instance.
  const [selectedRow, setSelectedRow] = useState<string>('');
  // The current page number for the instance table.
  const [currentPage, setCurrentPage] = useState<number>(1);
  // Number of instances to show per page.
  const [pageSize, setPageSize] = useState(TABLE_PAGE_SIZES[0]);
  // The list of STT/TTS instances shown in the current page.
  const [displayRows, setDisplayRows] = useState<SpeechRowInstance[]>([]);
  // Whether or not we have already moved the previously-selected
  // instance to the very top of the instance data table.
  const [hasMovedFirstInstance, setHasMovedFirstInstance] =
    useState<boolean>(false);

  /**
   * Finds an instance in the list with the specified ID (if any),
   * and moves that instance to the top of the list. The list is
   * edited in-place.
   *
   * @param {SpeechRowInstance[]} list - list of parsed instances
   * @param {string} instanceId - the ID of the instance to find/move
   * @returns {boolean} true if the list was modified, false otherwise
   */
  const moveSelectedInstanceToTop = (
    list: SpeechRowInstance[],
    instanceId: string
  ): boolean => {
    const findIndex = list.map(instance => instance.id).indexOf(instanceId);

    if (findIndex > -1) {
      const instance = _.cloneDeep(list[findIndex]);
      // delete the instance at the specified index
      list.splice(findIndex, 1);
      // add the instance back to the front of the array
      list.unshift(instance);

      return true;
    }
    return false;
  };

  /**
   * Converts standard STT/TTS instances to SpeechRowInstances,
   * and moves the instance already selected (defined in the config)
   * to the very top of the data table.
   */
  useEffect(() => {
    // This useEffect will only be effectively called once because
    // of this if-statement:
    // - Because `setParsedInstances` is called at the end of this
    //   method, `parsedInstances` will only be non-empty once.
    // - `instances` being empty will be ignored; hence, we parse
    //   the list as soon as it becomes non-empty and useful.
    if (!_.isEmpty(parsedInstances) || _.isEmpty(instances)) {
      return;
    }

    const mappedInstances = instances
      // instances with no credentials (apikey/url) cannot be used
      .filter(instance => !_.isEmpty(instance.credentials))
      .map(instance => ({
        id: instance.id,
        name: instance.entity?.name,
        plan: getSpeechPlanNameById(instance.plan?.guid),
        resource_group:
          instance.entity?.resource_group_name ||
          instance.entity?.resource_group_id ||
          'unknown',
        account:
          instance.entity?.account_name ||
          instance.entity?.account_id ||
          'unknown',
        region: instance.region
      }));

    // If a default/previously-selected instance is already defined,
    // find that instance in the instances list, move the instance
    // to the top, and then select the instance in the table. We
    // prevent future instance selections in the data table from
    // moving the instance to the very top (!hasMovedFirstInstance).
    if (!hasMovedFirstInstance && defaultSelection) {
      if (moveSelectedInstanceToTop(mappedInstances, defaultSelection)) {
        // check the selected instance's radiobox in the table
        setSelectedRow(defaultSelection);
        setHasMovedFirstInstance(true);
      }
    }

    setParsedInstances(mappedInstances);
  }, [defaultSelection, hasMovedFirstInstance, instances, parsedInstances]);

  /**
   * If the defaultSelection is first loaded (`selectedRow` is equal
   * to `defaultSelection` if defaultSelection was previously loaded)
   * then move the default/previously-selected instance to the top
   * of the table.
   * Note that for second and future changes of defaultSelection /
   * selectedRow, we do NOT move the selected instance to the top of
   * the list. Imagine you select an instance in page 46, and that
   * instance suddenly got moved to page one...
   */
  useEffect(() => {
    if (
      !loading &&
      !hasMovedFirstInstance &&
      !_.isEmpty(parsedInstances) &&
      selectedRow !== defaultSelection
    ) {
      if (moveSelectedInstanceToTop(parsedInstances, defaultSelection)) {
        // the parsedInstance list must be cloned, otherwise other
        // useEffects do not pick up that this object has changed
        const newParsedInstances = _.cloneDeep(parsedInstances);
        setParsedInstances(newParsedInstances);
        setHasMovedFirstInstance(true);
      }
    }

    setSelectedRow(defaultSelection);
  }, [
    defaultSelection,
    hasMovedFirstInstance,
    loading,
    parsedInstances,
    selectedRow,
    setParsedInstances
  ]);

  /**
   * Calculate from the currentPage index and render only instances
   * from the current page.
   */
  useEffect(() => {
    if (parsedInstances.length > 0) {
      const startIndex = (currentPage - 1) * pageSize;
      const endIndex = Math.min(parsedInstances.length, startIndex + pageSize);
      const currentDisplayRows = parsedInstances.slice(startIndex, endIndex);
      setDisplayRows(currentDisplayRows);
    }
  }, [currentPage, pageSize, parsedInstances]);

  /**
   * Process what to do when an instance on the table is selected.
   *
   * @param {string} instanceId - the ID of the instance selected
   * @param {Function} callbackFn - method to call when finished
   */
  const updateSelectedRow = useCallback(
    (instanceId: string, callbackFn: (id: string) => void) => {
      // we cannot change select if readOnly
      if (!readOnly) {
        callbackFn(instanceId);
        setSelectedRow(instanceId);
        handleSelect(instanceId);
      }
    },
    [handleSelect, readOnly]
  );

  const { formatMessage: t } = useIntl();
  const headers = [
    {
      key: 'name',
      header: t(messages.name)
    },
    {
      key: 'plan',
      header: t(messages.plan)
    },
    {
      key: 'resource_group',
      header: t(messages.resourceGroup)
    },
    {
      key: 'account',
      header: t(messages.account)
    },
    {
      key: 'region',
      header: t(messages.region)
    }
  ];

  return (
    <>
      <div
        className={cn({
          TextToSpeech__table: true,
          'Phone__SpeechInstance__table-container': true,
          'Phone__SpeechInstance__table-container-small': size === 'sm'
        })}>
        {loading || parsedInstances.length === 0 ? (
          <>
            <DataTableSkeleton
              showHeader={false}
              columnCount={size === 'sm' ? 1 : headers.length}
            />
          </>
        ) : (
          <DataTable
            rows={displayRows}
            headers={size === 'sm' ? [headers[0]] : headers}
            radio>
            {({
              rows,
              headers,
              getHeaderProps,
              getRowProps,
              getSelectionProps,
              getTableProps,
              _selectedRows,
              selectRow,
              getTableContainerProps
            }) => {
              return (
                <TableContainer {...getTableContainerProps()}>
                  <Table {...getTableProps()} size="sm">
                    <TableHead>
                      <TableRow>
                        <th scope="col" />
                        {headers.map((header, i) => (
                          <TableHeader key={i} {...getHeaderProps({ header })}>
                            {header.header}
                          </TableHeader>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.map((row, i) => (
                        <TableRow key={i} {...getRowProps({ row })}>
                          <TableSelectRow
                            {...getSelectionProps({ row })}
                            onSelect={() =>
                              updateSelectedRow(row.id, selectRow)
                            }
                            checked={selectedRow === row.id}
                          />
                          {row.cells.map(cell => (
                            <TableCell key={cell.id}>{cell.value}</TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {parsedInstances.length > pageSize && (
                    <Pagination
                      totalItems={parsedInstances.length}
                      size={size === 'sm' ? 'sm' : 'md'}
                      pageSize={pageSize}
                      pageSizes={TABLE_PAGE_SIZES}
                      onChange={change => {
                        setCurrentPage(change?.page);
                        setPageSize(change?.pageSize);
                      }}
                    />
                  )}
                </TableContainer>
              );
            }}
          </DataTable>
        )}
      </div>
    </>
  );
};
